import { getDisplayTimeZone } from "./zone.js";

/**
 * Every date and time the portal shows goes through here, so the project-wide rules hold in one place:
 *   - dates are Australian: day before month (05/10/2026, 5 Oct 2026), never the browser's own order
 *     (a browser set to US English would otherwise show "Oct 5");
 *   - times are 12-hour (2:05 pm), whatever the device's clock setting;
 *   - times are the BUSINESS's local time (its state's Australian time zone, lib/zone.ts), never the browser's: a phone set to another
 *     zone, or an admin looking at a business in another state, sees what the business sees.
 * Nothing else in the portal should call toLocaleString / toLocaleDateString / toLocaleTimeString on a date.
 * Two kinds of value: an INSTANT (an ISO timestamp or a Date: shown in the business zone) and a CALENDAR DATE or a WALL-CLOCK TIME of
 * day ("2026-10-05", "14:30"): those have no zone and are shown exactly as stored (the `...Only` and formatClock functions).
 * (Native <input type="date"> boxes follow the operating system's regional setting and can't be forced.)
 */

export const LOCALE = "en-AU";

const asDate = (value: string | number | Date): Date => (value instanceof Date ? value : new Date(value));

/** Formatting options for an instant: the business zone. */
const zoned = (): { timeZone: string } => ({ timeZone: getDisplayTimeZone() });
/** Formatting options for a calendar date held as a UTC-midnight Date: no zone shift. */
const calendar = { timeZone: "UTC" } as const;

/** A "YYYY-MM-DD" calendar date as a Date at UTC midnight, to be formatted with `calendar`. */
function calendarDate(ymd: string): Date {
  const [y = 1970, m = 1, d = 1] = ymd.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

/** "2:05 pm" */
export function formatTime(value: string | number | Date): string {
  return asDate(value).toLocaleTimeString(LOCALE, { ...zoned(), hour: "numeric", minute: "2-digit", hour12: true });
}

/** "2:05:09 pm" */
export function formatTimeWithSeconds(value: string | number | Date): string {
  return asDate(value).toLocaleTimeString(LOCALE, { ...zoned(), hour12: true });
}

/** "05/10/2026" */
export function formatDate(value: string | number | Date): string {
  return asDate(value).toLocaleDateString(LOCALE, zoned());
}

/** A date-only column ("2026-10-05") as "05/10/2026". Read as a calendar date, so no timezone shift. */
export function formatDateOnly(ymd: string): string {
  return calendarDate(ymd).toLocaleDateString(LOCALE, calendar);
}

/** "05/10/2026, 2:05:09 pm" */
export function formatDateTime(value: string | number | Date): string {
  return asDate(value).toLocaleString(LOCALE, { ...zoned(), hour12: true });
}

/** "5 Oct 2026, 2:05 pm" */
export function formatDateTimeMedium(value: string | number | Date): string {
  return asDate(value).toLocaleString(LOCALE, { ...zoned(), dateStyle: "medium", timeStyle: "short", hour12: true });
}

/** "Mon, 5 Oct 2026" */
export function formatWeekdayDate(value: string | number | Date): string {
  return asDate(value).toLocaleDateString(LOCALE, { ...zoned(), weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

/** "Mon, 5 Oct 2026" for a calendar date ("2026-10-05"). */
export function formatWeekdayDateOnly(ymd: string): string {
  return calendarDate(ymd).toLocaleDateString(LOCALE, { ...calendar, weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

/** "Monday 5 Oct" */
export function formatWeekdayDayMonth(value: string | number | Date): string {
  return asDate(value).toLocaleDateString(LOCALE, { ...zoned(), weekday: "long", day: "numeric", month: "short" });
}

/** "Monday 5 Oct" for a calendar date ("2026-10-05"). */
export function formatWeekdayDayMonthOnly(ymd: string): string {
  return calendarDate(ymd).toLocaleDateString(LOCALE, { ...calendar, weekday: "long", day: "numeric", month: "short" });
}

/** "Monday" */
export function formatWeekday(value: string | number | Date): string {
  return asDate(value).toLocaleDateString(LOCALE, { ...zoned(), weekday: "long" });
}

/** "Monday" for a calendar date ("2026-10-05"). */
export function formatWeekdayOnly(ymd: string): string {
  return calendarDate(ymd).toLocaleDateString(LOCALE, { ...calendar, weekday: "long" });
}

/** A stored "HH:MM" or "HH:MM:SS" wall-clock time ("14:30:00") as "2:30 pm". A wall-clock time has no zone, so none is applied. */
export function formatClock(hhmm: string): string {
  const [h = "0", m = "0"] = hhmm.split(":");
  return new Date(Date.UTC(2000, 0, 1, Number(h), Number(m))).toLocaleTimeString(LOCALE, { ...calendar, hour: "numeric", minute: "2-digit", hour12: true });
}
